import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  ClickResolveDetails,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js'
import axios from 'axios'
import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { isDesktop } from 'react-device-detect'
import Stripe from 'stripe'
import Button from '~/src/components/Button'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthUser from '~/src/hooks/useAuthUser'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import logger from '~/src/lib/logger'
import { ApplePayRegularBilling } from '~/src/lib/stripe'

interface CheckoutProps {
  amount: number
  planType?: 'yearly' | 'monthly'
  callback?: (data: Stripe.Subscription) => void
  displayApplePayButton?: (showing: boolean) => void
  processing?: boolean
}

const CheckoutButton = ({
  amount,
  callback,
  planType,
  displayApplePayButton,
  processing: processingIncoming,
}: CheckoutProps) => {
  const analytics = useAnalytics()
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [oneClickAvailable, setOneClickAvailable] = useState<
    boolean | undefined
  >(undefined) // State to track one-click availability

  const { featureOn: featureEnabled, loading: featureLoading } = useIsFeatureOn(
    FeatureFlags.PaywallStripeApplePay
  )

  const { loading, authUser } = useAuthUser()

  // If the user is on desktop, don't show the Apple Pay button
  useEffect(() => {
    if (isDesktop) {
      displayApplePayButton && displayApplePayButton(false)
    }
  }, [displayApplePayButton])

  useEffect(() => {
    if (typeof featureEnabled === 'undefined') return
    if (featureLoading) return

    if (!featureEnabled && !featureLoading) {
      displayApplePayButton && displayApplePayButton(false)
      return
    }

    if (typeof oneClickAvailable === 'undefined') return
    // If feature is not enabled, don't show the button

    if (!featureEnabled) {
      displayApplePayButton && displayApplePayButton(false)
      return
    }

    // Handle other cases - feature is enabled and one-click is available
    if (featureEnabled && oneClickAvailable) {
      setOneClickAvailable(true)
      displayApplePayButton && displayApplePayButton(true)
    } else {
      setOneClickAvailable(false)
      displayApplePayButton && displayApplePayButton(false)
    }
  }, [displayApplePayButton, featureEnabled, featureLoading, oneClickAvailable])

  useEffect(() => {
    if (!loading && !authUser) return

    // Fetch the user's email and phone number
    setEmail(authUser?.email || '')
    setPhone(authUser?.phoneNumber || '')
  }, [loading, authUser])

  const [email, setEmail] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const availableRef = useRef(false)

  const [processingCheckout, setProcessing] = useState(false)

  const processing = processingCheckout || processingIncoming

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (availableRef.current === true) {
        setOneClickAvailable(true)
      } else {
        setOneClickAvailable(false)
      }
    }, 1500)
    return () => clearTimeout(timeout)
  }, [])

  const onReady = ({
    availablePaymentMethods,
  }: {
    availablePaymentMethods: StripeExpressCheckoutElementReadyEvent['availablePaymentMethods']
  }) => {
    logger.debug('[Stripe Client] Available payment methods:', {
      availablePaymentMethods,
    })
    if (!availablePaymentMethods?.applePay) {
      // No buttons will show
      setOneClickAvailable(false)
    } else {
      // Apple Pay is supported
      setOneClickAvailable(true)
      availableRef.current = true
    }
  }

  const setupSubscription = async ({
    userEmail,
    userPhone,
    userName,
    confirmationTokenId,
    paymentMethodId,
  }: {
    userEmail: string
    userPhone: string
    userName: string
    confirmationTokenId?: string
    paymentMethodId?: string
  }) => {
    setProcessing(true)
    try {
      const payload = {
        confirmationTokenId,
        userEmail,
        userPhone,
        userName,
        subscription: true,
        paymentMethodId,
        planType,
      }

      const res = await axios.post('/api/stripe/create-intent', payload, {
        headers: {
          'x-authorization':
            authUser?.accessToken || (await authUser?.getIdToken()) || '',
        },
      })

      if (res.status === 500) {
        logger.error('[Stripe Client] status 500 creating token:', {
          res,
        })
        setErrorMessage('An error occurred, please try again')
        return
      }

      const data = await res.data
      const sub = data?.subscription as Stripe.Subscription

      // Send to callback
      callback && callback(sub)
    } catch (error) {
      logger.error('[Stripe Client] Error creating subscription:', {
        error,
      })
      setErrorMessage('An error occurred, please try again')
    } finally {
      setProcessing(false)
    }
  }

  const onConfirm = async (event: any) => {
    if (!stripe || !elements) {
      return
    }

    setProcessing(true)

    const userEmail = email || authUser?.email || event?.billingDetails?.email
    const userName = authUser?.displayName || event?.billingDetails?.name || ''
    const userPhone =
      phone || authUser?.phoneNumber || event?.billingDetails?.phone

    // Ensure we have an email
    if (!userEmail) {
      setErrorMessage('[Missing details] Please fill in all fields.')
      setProcessing(false)
      return
    }

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setErrorMessage(
          submitError?.message || 'An error occurred [elements.submit() error]'
        )
        logger.error('[Stripe Client] Error submitting elements:', {
          submitError,
        })
        setProcessing(false)
        return
      }

      const currentDomain = window.location.origin

      // ✅ Create a confirmation token - using the one-click flow
      const { error, confirmationToken } = await stripe.createConfirmationToken(
        {
          elements,
          params: {
            payment_method_data: {
              billing_details: {
                email,
                phone,
              },
            },
            return_url: `${currentDomain}/profile/manage-plan`,
          },
        }
      )

      if (error) {
        setErrorMessage(
          error?.message ||
            'An error occurred [error creating confirmation token]'
        )
        logger.error('[Stripe Client] Error creating confirmation token:', {
          error,
        })
        setProcessing(false)
        return
      }

      if (!confirmationToken?.id) {
        logger.error('[Stripe Client] Missing confirmation token:', {
          confirmationToken,
        })
        setErrorMessage('[Missing confirmation token] An error occurred')
        setProcessing(false)
        return
      }

      return setupSubscription({
        userEmail,
        userPhone,
        userName,
        confirmationTokenId: confirmationToken.id,
      })
    } catch (error) {
      logger.error('[Stripe Client] Error creating confirmation token:', {
        error,
      })
      setErrorMessage('An error occurred, please try again')
    }
  }

  const onCancel = () => {
    setProcessing(false)
    logger.debug('[Stripe Client] Checkout cancelled')
  }

  // get the exact date one month from now be specific - use interval to be exact
  const setTrialBilling = (
    plan: 'yearly' | 'monthly'
  ): {
    trialBilling: ApplePayRegularBilling
    regularBilling: ApplePayRegularBilling
  } => {

    // TODO: Could pull directly from pricing plan configs for trial length
    const thirtyDaysFromNow = new Date(
      new Date().setDate(new Date().getDate() + 30)
    )

    const sevenDaysFromNow = new Date(
      new Date().setDate(new Date().getDate() + 7)
    )

    return {
      trialBilling: {
        amount: 0,
        recurringPaymentIntervalUnit: plan === 'yearly' ? 'month' : 'day',
        recurringPaymentIntervalCount: plan === 'yearly' ? 1 : 7,
        label: 'Free Trial',
        recurringPaymentStartDate: new Date(),
        recurringPaymentEndDate:
          plan === 'yearly' ? thirtyDaysFromNow : sevenDaysFromNow,
      },
      regularBilling: {
        amount: amount,
        label: 'Subscription',
        recurringPaymentStartDate:
          plan === 'yearly' ? thirtyDaysFromNow : sevenDaysFromNow,
        recurringPaymentIntervalUnit: plan === 'yearly' ? 'year' : 'month',
        recurringPaymentIntervalCount: 1, // How often per unit should the payment be made e.g if interval unit is year and count is 1, then payment is made once a year or if interval unit is month and count is 3, then payment is made every 3 months
      },
    }
  }

  const onClick = ({
    resolve,
  }: {
    resolve: (options: ClickResolveDetails) => void
  }) => {
    setProcessing(true)
    analytics.track(AnalyticsEventName.ApplePayButtonClicked, {
      planType: planType || 'yearly',
      amount: amount,
    })

    const options: ClickResolveDetails = {
      emailRequired: true,
      applePay: {
        recurringPaymentRequest: {
          paymentDescription: 'Dupe Pro Subscription',
          managementURL: 'https://dupe.com/profile/manage-plan',
          ...setTrialBilling(planType || 'yearly'),
        },
      },
    }

    resolve(options)
  }

  const expressOptions: StripeExpressCheckoutElementOptions = {
    paymentMethods: {
      applePay: 'always',
      googlePay: 'never',
      link: 'never',
      amazonPay: 'never',
      paypal: 'never',
    },
    buttonHeight: 44,
  }

  if ((!featureEnabled && featureLoading) || isDesktop) {
    return null
  }

  return (
    <div>
      {errorMessage && oneClickAvailable && (
        <div className='mb-2 text-sm text-red-500'>{errorMessage}</div>
      )}

      {oneClickAvailable && processing && (
        // Loading indicator
        <Button disabled className='mx-auto mb-2 h-[44px] w-full' size='md'>
          <div className='animate-spin'>
            <LoaderIcon height={18} />
          </div>
        </Button>
      )}

      <div id='checkout-page'>
        <div
          className={clsx('mb-2', {
            hidden: !oneClickAvailable || processing,
          })}
        >
          <ExpressCheckoutElement
            onConfirm={onConfirm}
            onCancel={onCancel}
            onClick={onClick}
            onReady={onReady}
            options={expressOptions}
          />
        </div>

        {typeof oneClickAvailable === 'undefined' && (
          <Button disabled className='mb-2 h-[44px] w-full' size='md'>
            <div className='animate-spin'>
              <LoaderIcon height={18} />
            </div>
          </Button>
        )}
      </div>
    </div>
  )
}

export default CheckoutButton
